import { appState, setAuxConfiguration, useAppDispatch, useAppSelector } from "app";
import { EgerdauCustomer, useGetSAPAuxilaryDataQuery } from "common/services";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useEffect } from "react";
import { useEgerdauToken } from "./useLoadEgerdauToken";
import { datadogLogs } from "@datadog/browser-logs";

/**
 * Loads the SAP auxiliary data (sales org, currency, payment terms, incoterms) for the customer's
 * soldTo / shipTo into the `app` slice.
 *
 * @param customer the resolved customer, or null while the profile is still loading.
 * @param allowDegraded when true, a failed lookup resolves to a profile carrying only the
 *        soldTo / shipTo that were queried, leaving the SAP org fields blank. `appInitComplete`
 *        gates on `sapAuxProfile` being set, so without this the app would sit on its boot spinner
 *        whenever /customer/sold_to fails. Used on the Okta/BFF path, where a missing SAP org
 *        record must not stop the user from reaching the app.
 */
export const useLoadSAPData = (
  customer: EgerdauCustomer | null,
  allowDegraded: boolean = false,
) => {
  const dispatch = useAppDispatch();
  const defaultId = "0100948867";
  const { sapAuxProfile } = useAppSelector(appState);
  // All of the customer's ship-to identifiers, so the backend can match against any of them
  // (returning the first match). Falls back to the default id when the customer has none.
  const shipToIds: string[] =
    customer?.shipTo && customer.shipTo.length > 0
      ? customer.shipTo.map((address) => address.id || defaultId)
      : [defaultId];
  // The primary ship-to, stored on the resolved SAP aux profile for downstream consumers.
  const shipTo: string = shipToIds[0];
  const soldTo: string = customer?.soldToId || defaultId;

  const shouldFetchSAPAuxiliaryData = customer && !sapAuxProfile;

  const sapAuxiliaryDataParams = {
    shipTo: shipToIds,
    soldTo,
  };

  const token = useEgerdauToken();
  const { error, data } = useGetSAPAuxilaryDataQuery(
    shouldFetchSAPAuxiliaryData ? { token, args: sapAuxiliaryDataParams } : skipToken,
  );

  useEffect(() => {
    if (data) {
      dispatch(setAuxConfiguration({ ...data, shipTo, soldTo }));
    }
  }, [data, shipTo, soldTo, dispatch]);

  // Degraded fallback: keep the soldTo / shipTo we asked about and leave the SAP org fields blank,
  // so init can complete and the user reaches the app. Downstream consumers that forward this data
  // (the file upload's `customer_data`) will send blank org values rather than none at all.
  useEffect(() => {
    if (!allowDegraded || !error || sapAuxProfile) return;
    datadogLogs.logger.warn(
      "[SAP auxiliary data] lookup failed — continuing without the SAP org record",
      { error, soldTo, shipTo },
    );
    dispatch(
      setAuxConfiguration({
        salesOrg: "",
        distChannel: "",
        shippingCondition: "",
        incoTerms: "",
        currencyType: "",
        paymentTerms: "",
        shipTo,
        soldTo,
      }),
    );
  }, [allowDegraded, error, sapAuxProfile, shipTo, soldTo, dispatch]);

  return {
    sapAuxProfile,
    error,
  };
};
